import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ReactElement, useState } from "react";
import { Button } from "../../components/ui/button";
import { delierViewerReponseSchema, type RenommeeFiche } from "../contract";
import { Rang } from "./rang";
import {
  DELIER,
  dateDeLiaison,
  libelleMotif,
  MOTIFS,
  points,
  RENOMMEE,
  STATS_LIBELLES,
} from "./viewers-texts";

/**
 * Le bloc « Sa renommee » de la fiche d'un viewer (ticket 2 du socle) : son rang, ses points, ce qu'il lui manque
 * pour le suivant, d'ou viennent ses points, ses stats de carte et, s'il y en a un, le plancher que la taille de sa
 * chaine lui donne. `renommee` nul : le viewer n'a pas de profil, il est Ecuyer a 0.
 *
 * Les memes classes que les autres encarts de la fiche (`fiche-viewer.tsx`) : un bloc de plus sur la fiche validee en
 * Q1, sans maquette dediee. Tout vient de `viewers-texts.ts` ; les nombres sont ecrits a la francaise.
 */

const ENCART = "flex flex-col gap-3 rounded-md border-2 border-border bg-background px-5 py-4";
const TITRE_ENCART = "police-titre m-0 text-[28px] font-normal text-primary";

/** Les motifs a montrer, dans l'ordre de `MOTIFS`, puis ceux que ce code ne connait pas (par ordre alphabetique). */
function motifsDuDetail(detail: Readonly<Record<string, number>>): string[] {
  const connus = MOTIFS.filter((motif) => (detail[motif] ?? 0) > 0);
  const autres = Object.keys(detail)
    .filter((motif) => !(MOTIFS as readonly string[]).includes(motif) && (detail[motif] ?? 0) > 0)
    .sort();
  return [...connus, ...autres];
}

function Cellule({ label, children }: { label: string; children: string }): ReactElement {
  return (
    <div className="rounded-[4px] border-2 border-border bg-card px-3 py-2">
      <dt className="text-[19px] text-muted-foreground">{label}</dt>
      <dd className="police-nom m-0 text-[34px] leading-none text-[#FCDD8E]">{children}</dd>
    </div>
  );
}

/** `POST /api/viewers/{id}/delier` : `delie` ou `pas_relie`, rien d'autre. */
async function delierRequest(viewerId: string) {
  const response = await fetch(`/api/viewers/${encodeURIComponent(viewerId)}/delier`, {
    method: "POST",
    headers: { accept: "application/json" },
  });
  const payload: unknown = await response.json().catch(() => null);
  const result = delierViewerReponseSchema.safeParse(payload);
  if (!response.ok || !result.success) {
    throw new Error("delier_failed");
  }
  return result.data.issue;
}

/** Le bouton « Délier », avec sa confirmation : defaire la liaison ne fait baisser personne, mais ne se refait que par un nouveau code. */
function Delier({ viewerId }: { viewerId: string }): ReactElement {
  const [confirmation, setConfirmation] = useState(false);
  const client = useQueryClient();
  const delier = useMutation({
    mutationFn: () => delierRequest(viewerId),
    onSettled: () => setConfirmation(false),
    onSuccess: () => client.invalidateQueries({ queryKey: ["direct", "viewer", viewerId] }),
  });
  if (delier.isSuccess) {
    return (
      <p role="status" className="m-0 text-muted-foreground">
        {delier.data === "delie" ? DELIER.delie : DELIER.pasRelie}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {confirmation ? (
        <>
          <p role="alert" className="m-0">
            {DELIER.question}
          </p>
          <div className="flex gap-2">
            <Button type="button" autoFocus onClick={() => setConfirmation(false)}>
              {DELIER.annuler}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={delier.isPending}
              onClick={() => delier.mutate()}
            >
              {delier.isPending ? DELIER.enCours : DELIER.confirmer}
            </Button>
          </div>
        </>
      ) : (
        <div>
          <Button type="button" variant="outline" onClick={() => setConfirmation(true)}>
            {DELIER.bouton}
          </Button>
        </div>
      )}
      {delier.isError ? (
        <p role="alert" className="m-0 text-muted-foreground">
          {DELIER.echec}
        </p>
      ) : null}
    </div>
  );
}

export function BlocRenommee({
  renommee,
  viewerId,
}: {
  renommee: RenommeeFiche | null;
  viewerId?: string | undefined;
}): ReactElement {
  if (renommee === null) {
    return (
      <section className={ENCART} aria-labelledby="fiche-renommee">
        <h3 id="fiche-renommee" className={TITRE_ENCART}>
          {RENOMMEE.titre}
        </h3>
        <p className="m-0 text-muted-foreground">{RENOMMEE.sans}</p>
      </section>
    );
  }
  const motifs = motifsDuDetail(renommee.detail);
  const { stats } = renommee;
  return (
    <section className={ENCART} aria-labelledby="fiche-renommee">
      <h3 id="fiche-renommee" className={TITRE_ENCART}>
        {RENOMMEE.titre}
      </h3>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Rang id={renommee.rang} />
        <span className="police-nom text-[34px] leading-none text-[#FCDD8E]">
          {RENOMMEE.total(renommee.renommee)}
        </span>
      </div>
      <p className="m-0 text-muted-foreground">
        {renommee.prochain === null
          ? RENOMMEE.sommet
          : RENOMMEE.prochain(renommee.prochain.reste, renommee.prochain.rang)}
      </p>
      {renommee.plancher !== null && renommee.plancher !== "ecuyer" ? (
        <p className="m-0 text-muted-foreground">{RENOMMEE.plancher(renommee.plancher)}</p>
      ) : null}
      {renommee.discord === null ? null : (
        <>
          <p className="m-0 break-words text-muted-foreground">
            {RENOMMEE.discord(renommee.discord.pseudo, dateDeLiaison(renommee.discord.relieLe))}
          </p>
          {viewerId === undefined ? null : <Delier viewerId={viewerId} />}
        </>
      )}

      {motifs.length === 0 ? null : (
        <>
          <h4 className="m-0 border-b border-border pb-1 text-[19px] font-normal text-primary">
            {RENOMMEE.detailTitre}
          </h4>
          <ul aria-label={RENOMMEE.detailTitre} className="m-0 flex list-none flex-col gap-1 p-0">
            {motifs.map((motif) => (
              <li key={motif} className="flex items-baseline justify-between gap-4">
                <span>{libelleMotif(motif)}</span>
                <span className="police-nom text-[26px] text-[#FCDD8E]">
                  {points(renommee.detail[motif] ?? 0)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <h4 className="m-0 border-b border-border pb-1 text-[19px] font-normal text-primary">
        {RENOMMEE.statsTitre}
      </h4>
      <dl aria-label={RENOMMEE.statsTitre} className="m-0 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Cellule label={STATS_LIBELLES.passages}>{points(stats.passages)}</Cellule>
        <Cellule label={STATS_LIBELLES.messages}>{points(stats.messages)}</Cellule>
        <Cellule label={STATS_LIBELLES.moisDeSub}>{points(stats.moisDeSub)}</Cellule>
        {stats.raids > 0 ? (
          <Cellule label={STATS_LIBELLES.raids}>{points(stats.raids)}</Cellule>
        ) : null}
      </dl>
    </section>
  );
}
